import React from 'react';

// The card shell shared by the web tiers (PricingTiers) and the native App
// Store plans (AppStorePlans), so both pricing surfaces look the same. Each
// caller supplies its own action area as children.

interface PlanCardProps {
  name: string;
  tagline?: string;
  price: string;
  priceSuffix?: string;
  // A line under the price, e.g. how the App Store bills it.
  priceNote?: React.ReactNode;
  features: string[];
  // Outlines the card as the visitor's own plan.
  highlighted?: boolean;
  // 'modal' is the compact layout used inside the usage-limit modal.
  variant?: 'modal' | 'page';
  isDarkMode: boolean;
  children: React.ReactNode;
}

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden className="h-3 w-3">
    <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const PlanCard: React.FC<PlanCardProps> = ({
  name,
  tagline,
  price,
  priceSuffix,
  priceNote,
  features,
  highlighted = false,
  variant = 'page',
  isDarkMode,
  children,
}) => {
  const compact = variant === 'modal';
  const surface = isDarkMode
    ? 'bg-white/[0.04]'
    : 'bg-true-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_32px_-16px_rgba(0,0,255,0.18)]';
  // The highlight recolours the card's own 1px border rather than adding a ring.
  const border = highlighted
    ? isDarkMode
      ? 'border-blue-400'
      : 'border-blue-700'
    : isDarkMode
      ? 'border-white/10'
      : 'border-black-100';
  const mutedText = isDarkMode ? 'text-black-300' : 'text-black-500';

  return (
    <div
      className={`flex flex-col rounded-3xl border transition-colors duration-300 ${surface} ${border} ${
        compact ? 'p-5' : 'p-7 lg:p-8'
      }`}
    >
      <h3
        className={`portrait-text-app ${compact ? 'text-2xl' : 'text-3xl lg:text-4xl'} ${
          isDarkMode ? 'text-white-custom' : 'text-blue-700'
        }`}
      >
        {name}
      </h3>
      {tagline && <p className={`mt-2 text-sm ${mutedText}`}>{tagline}</p>}

      <div className={`flex items-baseline gap-1 ${compact ? 'mt-4' : 'mt-6'}`}>
        <span
          className={`font-semibold tracking-tight tabular-nums ${compact ? 'text-3xl' : 'text-4xl lg:text-5xl'} ${
            isDarkMode ? 'text-white-custom' : 'text-black-1500'
          }`}
        >
          {price}
        </span>
        {priceSuffix && <span className={`text-sm ${mutedText}`}>{priceSuffix}</span>}
      </div>
      {priceNote && <p className={`mt-2 text-xs leading-relaxed ${mutedText}`}>{priceNote}</p>}

      <hr className={`border-t ${compact ? 'my-4' : 'my-6'} ${isDarkMode ? 'border-white/10' : 'border-black-100'}`} />

      <ul className={`flex-1 space-y-3 text-sm ${compact ? 'mb-5' : 'mb-8'} ${isDarkMode ? 'text-black-200' : 'text-black-900'}`}>
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                isDarkMode ? 'bg-blue-400/15 text-blue-300' : 'bg-blue-50 text-blue-700'
              }`}
            >
              <CheckIcon />
            </span>
            <span className="leading-relaxed">{feature}</span>
          </li>
        ))}
      </ul>

      {children}
    </div>
  );
};

export const planButtonClasses =
  'w-full rounded-full bg-blue-700 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-800 disabled:cursor-default disabled:opacity-60 cursor-pointer';

// A pill in place of the action button, e.g. "Your current plan".
export const planStatusClasses = (isDarkMode: boolean, tone: 'neutral' | 'accent' = 'neutral'): string =>
  // py-[11px] plus the 1px border keeps the pill the same height as the buttons.
  `rounded-full border px-4 py-[11px] text-center text-sm font-medium ${
    tone === 'accent'
      ? isDarkMode
        ? 'border-blue-400/20 bg-blue-400/15 text-blue-300'
        : 'border-blue-100 bg-blue-50 text-blue-700'
      : isDarkMode
        ? 'border-white/8 bg-white/5 text-black-300'
        : 'border-black-200 bg-black-100 text-black-600'
  }`;

export default PlanCard;
